import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import AACChromeAPI from '@amzn/aac-chrome-api';
import { ErrorIdProvider } from '@amzn/storm-ui';
import { ConnectedChromeApp } from './commons/components/ChromeApp/ChromeApp';
import { ConnectedNav } from './features/nav/Nav';
import { ConnectedHeader } from './features/header/Header';
import { ConnectedPivotNav } from './features/pivotNav/PivotNav';
import { Footer } from './features/footer/Footer';
import { initFooterState } from './features/footer/store/slice';
import { initHeaderState } from './features/header/store/slice';
import { store } from './app/store';
import { ChromeConfigContextProvider } from 'src/providers/ChromeConfigContextProvider';
import { InternalChromeConfigContextProvider } from 'src/providers/InternalChromeConfigContextProvider';
import {
  initPerformanceLogger,
  performanceMark,
} from './utils/performance-helper';
import {
  initTakt,
  markAACChromeReady,
  markComponentStart,
} from './commons/utils/logWithTakt';
import { CHROME_APP_NAME, Components } from './commons/constants';
import { NodeEnv } from 'src/constants';
import './remoteComponentEntries.ts';
import { loadHmhBundleScript } from './features/helpmehere/loadHmhBundleScript';
import {
  HeaderBundleProvider,
  PageHeaderBundleProvider,
} from './providers/PantherLocalizationProvider';
import { PivotNavBundleProvider } from './providers/PantherLocalizationProvider/PantherLocalizationProvider';
import { markComponentRendered } from './utils/component-initialization';
import './web-components';
import './window';
import undefinedUrlLogger from './utils/undefined-url-logger';
import { localDevPage } from 'src/app/devPage/devPage';
import { initBrowserTracing } from '@amzn/pantheon-global-tracing/browser';
import { initMidwaySessionRefresh } from 'src/commons/utils/midwaySessionRefresh';

try {
  const applicationName = document
    .querySelector('meta[name="a2z:application_name"]')
    ?.getAttribute('content');

  initBrowserTracing({ serviceName: applicationName || undefined });
} catch (e) {
  console.warn(`Failed to initialize PantheonGlobalTracing.`, e);
}

initPerformanceLogger();
initTakt();

// add loggers for undefined urls
undefinedUrlLogger();

initMidwaySessionRefresh();

// Only run localDevPage in development environment
if (
  process.env.NODE_ENV === NodeEnv.development &&
  window.location.pathname === '/'
) {
  localDevPage();
}

export const initChromeHeader = (headerContainerId, headerData) => {
  // remove once backend removed accountInfo in prod
  if (headerData.accountInfo) {
    headerData.currentAccountInfo = headerData.accountInfo;
  }
  markComponentStart(Components.HEADER);
  const endMarker = performanceMark('aac-chrome-ui-header');
  store.dispatch(initHeaderState(headerData));

  ReactDOM.render(
    <ErrorIdProvider id="Chrome">
      <Provider store={store}>
        <InternalChromeConfigContextProvider>
          <ChromeConfigContextProvider>
            <ConnectedChromeApp name={CHROME_APP_NAME.Header}>
              <HeaderBundleProvider>
                <PageHeaderBundleProvider>
                  <ConnectedHeader />
                </PageHeaderBundleProvider>
              </HeaderBundleProvider>
            </ConnectedChromeApp>
          </ChromeConfigContextProvider>
        </InternalChromeConfigContextProvider>
      </Provider>
    </ErrorIdProvider>,
    document.getElementById(headerContainerId),
  );
  endMarker();
  markComponentRendered('header');
};

export const initChromeNav = (navContainerId, navData) => {
  // remove once backend removed accountInfo in prod
  if (navData.accountInfo) {
    navData.currentAccountInfo = navData.accountInfo;
  }
  markComponentStart(Components.NAV);
  const endMarker = performanceMark('aac-chrome-ui-nav');

  ReactDOM.render(
    <ErrorIdProvider id="Chrome">
      <Provider store={store}>
        <InternalChromeConfigContextProvider>
          <ChromeConfigContextProvider>
            <ConnectedChromeApp name={CHROME_APP_NAME.Nav}>
              <ConnectedNav navData={navData} />
            </ConnectedChromeApp>
          </ChromeConfigContextProvider>
        </InternalChromeConfigContextProvider>
      </Provider>
    </ErrorIdProvider>,
    document.getElementById(navContainerId),
  );
  endMarker();
  markComponentRendered('nav');
};

export const initChromePivotNav = (pivotNavContainerId) => {
  markComponentStart(Components.PIVOT_NAV);
  const endMarker = performanceMark('aac-chrome-ui-pivot-nav');
  ReactDOM.render(
    <ErrorIdProvider id="Chrome">
      <Provider store={store}>
        <InternalChromeConfigContextProvider>
          <ChromeConfigContextProvider>
            <ConnectedChromeApp name={CHROME_APP_NAME.PivotNav}>
              <PivotNavBundleProvider>
                <ConnectedPivotNav />
              </PivotNavBundleProvider>
            </ConnectedChromeApp>
          </ChromeConfigContextProvider>
        </InternalChromeConfigContextProvider>
      </Provider>
    </ErrorIdProvider>,
    document.getElementById(pivotNavContainerId),
  );
  endMarker();
  markComponentRendered('pivotNav');
};

export const initChromeFooter = (footerContainerId, footerData) => {
  markComponentStart(Components.FOOTER);
  // initChromeFooter is called at last among all the components
  // Marking AACChrome ready since all its dependent comopents are registered now
  markAACChromeReady();
  const endMarker = performanceMark('aac-chrome-ui-footer');
  store.dispatch(initFooterState(footerData));

  ReactDOM.render(
    <ErrorIdProvider id="Chrome">
      <Provider store={store}>
        <InternalChromeConfigContextProvider>
          <ChromeConfigContextProvider>
            <ConnectedChromeApp name={CHROME_APP_NAME.Footer}>
              <Footer />
            </ConnectedChromeApp>
          </ChromeConfigContextProvider>
        </InternalChromeConfigContextProvider>
      </Provider>
    </ErrorIdProvider>,
    document.getElementById(footerContainerId),
  );

  AACChromeAPI.getCommonEventTarget().addEventListener(
    'ChromeConfigurationChange',
    loadHmhBundleScript,
  );

  endMarker();
  markComponentRendered('footer');
};
